import React from "react";
import { AlertCircle, RotateCcw, Home } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

/**
 * Phase 9 — friendly fallback for the review page so a single render-time
 * error doesn't blank the whole screen. Surfaces the underlying message in
 * dev so we can debug, hides it in prod.
 */
export class ReviewErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { error: null };
    }
    static getDerivedStateFromError(error) {
        return { error };
    }
    componentDidCatch(error, info) {
        // eslint-disable-next-line no-console
        console.error("ReviewErrorBoundary caught:", error, info);
    }
    handleRetry = () => {
        this.setState({ error: null });
        // Soft retry — if the error is in stale state, re-mount via reload.
        window.location.reload();
    };
    render() {
        if (!this.state.error) return this.props.children;
        const msg = String(this.state.error?.message || this.state.error || "Unknown render error");
        return (
            <div className="mx-auto max-w-xl py-16 text-center space-y-4" data-testid="review-error-boundary">
                <AlertCircle className="mx-auto h-10 w-10 text-destructive" />
                <h1 className="text-2xl font-semibold text-foreground">
                    Something went wrong while rendering this review
                </h1>
                <p className="text-sm text-muted-foreground">
                    The review was saved — this is just a display hiccup. Try refreshing the page.
                </p>
                {process.env.NODE_ENV !== "production" && (
                    <pre className="mx-auto max-w-md overflow-auto rounded-md bg-muted p-3 text-left text-xs text-muted-foreground">
                        {msg}
                    </pre>
                )}
                <div className="flex justify-center gap-2 pt-2">
                    <Button onClick={this.handleRetry} data-testid="review-error-retry">
                        <RotateCcw className="h-4 w-4" />
                        Reload page
                    </Button>
                    <Button asChild variant="outline">
                        <Link to="/"><Home className="h-4 w-4" />Back to home</Link>
                    </Button>
                </div>
            </div>
        );
    }
}
